import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from '@vendasta/businesses';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Papa } from 'ngx-papaparse';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, share, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { downloadFile } from '../../../concierge/core/utils';
import { BULK_USER_IMPORT_CSV } from '../../../constants';
import { PartnerService } from '../../../core/partner.service';
import { VConfigService } from '../../../vconfig/vconfig.service';
import { BulkUserImportService, UserList } from './bulk-user-import.service';

const MAX_USERS = 1000;
const MAX_VMF_USERS = 55000;

export enum CreationType {
  CREATE_NEW_LIST = 1,
  USE_EXISTING_LIST = 2,
  SKIP = 3,
}
@Component({
  templateUrl: './bulk-user-import.component.html',
  styleUrls: ['./bulk-user-import.component.scss'],
  standalone: false,
})
export class BulkUserImportComponent implements OnInit, OnDestroy {
  @ViewChild('stepper', { static: true })
  pageStepper: MatStepper;
  fileFormControl: UntypedFormControl;
  fileForm: UntypedFormGroup;
  fileProcessing = false;
  fileProcessingErrors = false;
  fileProcessingError: string[] = [];
  fileProcessingErrorRows: string[] = [];
  fileProcessingErrorOverflow: string[] = [];
  fileProcessingErrorOverflowShowHide: number[] = [];
  fileProcessingErrorRowCount = 5; // threshold for # of rows of displayable issues (+1~)
  loading = false;
  csvFileName: string;
  csvUserImportTotal = 0;
  csvErrorRows: any = [];
  vbcConfigKey = 'vbc_product_name';
  userListForm: UntypedFormGroup;
  userListName: string;
  importErrorMessage: string;
  updateUsers = false;
  showUpdateUsersDetails = false;
  listBehavior: CreationType = CreationType.USE_EXISTING_LIST;
  ct: typeof CreationType = CreationType;

  userUploadInstructions: string;
  rowTotalText: string;
  selectFileText: string;

  userListList$: Observable<UserList[]>;
  filteredUserListList$: Observable<UserList[]>;
  userListCount = 200;
  userListId: string;
  isVMF = false;

  subscriptions: Subscription[] = [];

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    @Inject('MARKET_ID') private readonly marketId$: Observable<string>,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private bulkUserImportService: BulkUserImportService,
    private vConfigService: VConfigService,
    private csvParser: Papa,
    private route: ActivatedRoute,
    private partnerService: PartnerService,
    private feature: FeatureFlagService,
    private translate: TranslateService,
    private snowplowService: ProductAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.createFormControls();

    this.subscriptions.push(
      this.partnerId$
        .pipe(
          switchMap((partnerId) => {
            if (partnerId === 'VMF') {
              this.isVMF = true;
            }
            return this.vConfigService.getConfigurations(partnerId, [this.vbcConfigKey]);
          }),
          map((config) => {
            this.userUploadInstructions = this.translate.instant(
              'USERS.BULK_IMPORT.UPLOAD_FILE_STEP.IMPORT_DETAILS_A',
              { businessCenterName: config.vbc_product_name },
            );
          }),
          shareReplay(1),
        )
        .subscribe(),
    );

    this.selectFileText = this.translate.instant('USERS.BULK_IMPORT.UPLOAD_FILE_STEP.SELECT_FILE');

    this.userListList$ = this.bulkUserImportService.listUserLists(this.userListCount).pipe(share());
    const userFilter$ = this.userListForm.get('selectList').valueChanges.pipe(startWith(''));
    this.filteredUserListList$ = combineLatest([this.userListList$, userFilter$]).pipe(
      map(([userLists, searchTerm]) => {
        if (!searchTerm) {
          return userLists;
        }
        return userLists.filter((userList) => userList.userListName.toLowerCase().includes(searchTerm.toLowerCase()));
      }),
    );

    this.subscriptions.push(
      this.userListList$
        .pipe(
          map((userLists) => {
            if (userLists.length === 0) {
              this.listBehavior = CreationType.CREATE_NEW_LIST;
              this.createNewUserList();
            } else {
              this.useExistingList();
            }
          }),
        )
        .subscribe(),
    );
  }

  private createFormControls(): void {
    this.fileFormControl = new UntypedFormControl('', [Validators.required]);

    this.fileForm = this.formBuilder.group({
      file: ['', Validators.required],
      fileHasErrors: [false],
    });
    this.userListForm = this.formBuilder.group({
      createNewList: [],
      selectList: [null, Validators.required],
      userListName: [null],
    });
  }

  fileOnChange(event: any): void {
    this.csvErrorRows = [];
    if (!event.target.files) {
      return;
    }
    this.resetFileProcessingErrors();
    this.bulkUserImportService.file = event.target.files[0];
    const fileReader = new FileReader();
    if (this.bulkUserImportService.file) {
      fileReader.readAsText(this.bulkUserImportService.file);
      this.csvFileName = this.bulkUserImportService.file.name;
      this.fileProcessing = true;
    }
    fileReader.onload = () => {
      const fileContent = fileReader.result.toString();
      if (!this.parseFileContent(fileContent)) {
        return;
      }

      this.subscriptions.push(
        this.bulkUserImportService
          .uploadCsv(fileContent)
          .pipe(switchMap(() => this.bulkUserImportService.validateCSV()))
          .subscribe({
            next: (validationErrors) => {
              if (validationErrors.length > 0) {
                this.fileProcessingErrors = true;
                this.surfaceProcessingErrors(validationErrors);
                return;
              }
              this.goToUserListStep();

              this.fileProcessing = false;
            },
            error: () => {
              this.surfaceProcessingErrors([this.translate.instant('USERS.BULK_IMPORT.UPLOAD_FILE_STEP.UPLOAD_ERROR')]);
            },
          }),
      );
    };
  }

  parseFileContent(fileContent: string): boolean {
    const parsedContent = this.csvParser.parse(fileContent, {
      skipEmptyLines: 'greedy',
    });
    this.csvUserImportTotal = parsedContent.data.length === 0 ? 0 : parsedContent.data.length - 1;
    this.rowTotalText =
      this.csvUserImportTotal === 1
        ? this.translate.instant('USERS.BULK_IMPORT.REVIEW_STEP.ROW_TOTAL_SINGULAR')
        : this.translate.instant('USERS.BULK_IMPORT.REVIEW_STEP.ROW_TOTAL_PLURAL', {
            numUsers: this.csvUserImportTotal,
          });

    if (this.csvUserImportTotal === 0) {
      this.fileProcessingErrors = true;
      this.surfaceProcessingErrors([this.translate.instant('USERS.BULK_IMPORT.UPLOAD_FILE_STEP.MIN_SIZE_ERROR')]);
      return false;
    }
    if (
      (this.csvUserImportTotal > MAX_USERS && !this.isVMF) ||
      (this.isVMF && this.csvUserImportTotal > MAX_VMF_USERS)
    ) {
      this.fileProcessingErrors = true;
      this.surfaceProcessingErrors([
        this.translate.instant('USERS.BULK_IMPORT.UPLOAD_FILE_STEP.MAX_SIZE_ERROR', {
          maxRows: this.isVMF ? MAX_VMF_USERS : MAX_USERS,
        }),
      ]);
      return false;
    }
    return true;
  }

  surfaceProcessingErrors(errors: string[]): void {
    for (const error of errors) {
      if (error.includes('Row')) {
        const split = error.indexOf(' ', error.indexOf(' ') + 1);
        const rows = error.substr(0, split).replace(/,/g, ', ');
        if (this.occurrences(rows, ',') > this.fileProcessingErrorRowCount) {
          this.fileProcessingErrorRows.push(
            rows.substr(0, rows.split(',', this.fileProcessingErrorRowCount).join(',').length) + '...:',
          );
          this.fileProcessingErrorOverflow.push(rows.substr(rows.indexOf(' ') + 1).slice(0, -1));
          this.fileProcessingErrorOverflowShowHide.push(0);
        } else {
          this.fileProcessingErrorRows.push(rows);
          this.fileProcessingErrorOverflow.push('');
          this.fileProcessingErrorOverflowShowHide.push(-1);
        }
        this.fileProcessingError.push(error.substr(split));
      } else {
        this.fileProcessingErrorRows.push('');
        this.fileProcessingErrorOverflow.push('');
        this.fileProcessingErrorOverflowShowHide.push(-1);
        this.fileProcessingError.push(error);
      }
    }
    this.fileProcessing = false;
  }

  occurrences(mainString: string, subString: string): number {
    mainString += '';
    subString += '';
    if (subString.length <= 0) {
      return mainString.length + 1;
    }
    let n = 0,
      pos = 0;
    const step = subString.length;
    for (;;) {
      pos = mainString.indexOf(subString, pos);
      if (pos >= 0) {
        ++n;
        pos += step;
      } else {
        break;
      }
    }
    return n;
  }

  showMoreOrLess(index: number): void {
    if (this.fileProcessingErrorOverflowShowHide[index] === 0) {
      this.fileProcessingErrorOverflowShowHide[index] = 1;
    } else {
      this.fileProcessingErrorOverflowShowHide[index] = 0;
    }
  }

  resetFileProcessingErrors(): void {
    this.fileProcessingError = [];
    this.fileProcessingErrorRows = [];
    this.fileProcessingErrorOverflow = [];
    this.fileProcessingErrorOverflowShowHide = [];
    this.fileProcessingErrors = false;
  }

  clearInput(event: any, input: any): void {
    input.value = '';
    this.surfaceProcessingErrors([]);
  }

  useExistingList(): void {
    this.listBehavior = CreationType.USE_EXISTING_LIST;
    this.userListForm.controls.selectList.setValidators(Validators.required);
    this.userListForm.controls.userListName.clearAsyncValidators();
    this.userListForm.controls.userListName.clearValidators();
    this.userListForm.controls.userListName.updateValueAndValidity({
      onlySelf: true,
    });
    this.userListForm.controls.selectList.updateValueAndValidity({
      onlySelf: true,
    });
  }

  createNewUserList(): void {
    this.listBehavior = CreationType.CREATE_NEW_LIST;
    this.userListForm.controls.selectList.setValue(null);
    this.userListForm.controls.selectList.clearValidators();
    this.userListForm.controls.selectList.clearAsyncValidators();
    this.userListForm.controls.userListName.setAsyncValidators(this.noWhitespaceValidator);
    this.userListForm.controls.userListName.setValue(null);
    this.userListForm.controls.userListName.updateValueAndValidity();
    this.userListForm.controls.selectList.updateValueAndValidity();

    this.clearUserListSelect();
  }

  skipCreateUserLists(): void {
    this.createNewUserList();
    this.userListForm.controls.selectList.clearValidators();
    this.userListForm.controls.userListName.clearAsyncValidators();
    this.userListForm.controls.userListName.clearValidators();
    this.userListForm.controls.userListName.updateValueAndValidity({
      onlySelf: true,
    });
    this.userListForm.controls.createNewList.setValue(false);
    this.listBehavior = CreationType.SKIP;
  }

  goToUserListStep(): void {
    this.fileForm.controls.file.markAsTouched();
    this.fileForm.controls.fileHasErrors.markAsTouched();
    this.resetFileProcessingErrors();
    this.pageStepper.next();
  }

  goToFinalStep(): void {
    this.pageStepper.next();
    this.importErrorMessage = null;
  }

  downloadTemplate(): void {
    const csvBlob = new Blob([BULK_USER_IMPORT_CSV], { type: 'text/csv' });
    downloadFile(csvBlob, 'user-import-template.csv');
  }

  startListImport(): void {
    this.loading = true;
    if (this.listBehavior === CreationType.CREATE_NEW_LIST) {
      const listId = uuidv4();
      const errorMessage =
        this.csvUserImportTotal === 1
          ? this.translate.instant('USERS.BULK_IMPORT.REVIEW_STEP.IMPORT_ERROR_SINGULAR')
          : this.translate.instant('USERS.BULK_IMPORT.REVIEW_STEP.IMPORT_ERROR_PLURAL');
      this.subscriptions.push(
        this.bulkUserImportService.createUserList(this.userListName, listId).subscribe({
          next: (listCreated) => {
            if (listCreated) {
              this.bulkUserImportService.startUserImport(this.csvUserImportTotal, listId, this.updateUsers);
              this.uploadSuccess();
            } else {
              this.importErrorMessage = errorMessage;
            }
          },
          error: () => (this.importErrorMessage = errorMessage),
        }),
      );
    } else if (this.listBehavior === CreationType.USE_EXISTING_LIST) {
      this.bulkUserImportService.startUserImport(this.csvUserImportTotal, this.userListId, this.updateUsers);
      this.uploadSuccess();
    } else {
      this.bulkUserImportService.startUserImport(this.csvUserImportTotal, null, this.updateUsers);
      this.uploadSuccess();
    }
  }

  navigateToManageUsers(): void {
    this.router.navigate(['/bc-admin']);
  }

  navigateToUserLists(): void {
    this.router.navigate(['action-lists', 'manage', 'user-list']);
  }

  uploadSuccess(): void {
    if (this.listBehavior === CreationType.CREATE_NEW_LIST || this.listBehavior === CreationType.USE_EXISTING_LIST) {
      this.navigateToUserLists();
    } else {
      this.navigateToManageUsers();
    }
  }

  noWhitespaceValidator(control: UntypedFormControl): Observable<any> {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? of(null) : of({ whitespace: true });
  }

  toggleUpdateUsersDetails(): void {
    this.showUpdateUsersDetails = !this.showUpdateUsersDetails;
  }

  clearUserListSelect(): void {
    this.userListId = null;
  }

  setListName(userListName: any): void {
    if (this.listBehavior === CreationType.CREATE_NEW_LIST) {
      this.userListName = (userListName.target as HTMLInputElement)?.value;
    } else if (this.listBehavior === CreationType.USE_EXISTING_LIST) {
      this.userListName = userListName.userListName;
      this.userListId = userListName.listId;
    }
  }

  setListNameOption(userListName: any, event: any): void {
    if (event.isUserInput) {
      this.setListName(userListName);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
